/**
 * Drain confirmation dialog (#256).
 *
 * Two-stage safety prompt for the admin-tier "Drain node" action:
 *   1. On open, fetches `/drain-preview` to enumerate the pods that would
 *      be evicted (and which of them belong to show-affecting workloads).
 *   2. If `criticalCount > 0` (any show-affecting Deployment/STS pods on
 *      the node), the operator must type the node name to confirm — same
 *      pattern the Reset dialog uses. Non-critical drains get a plain
 *      confirmation.
 *
 * The actual eviction is dispatched by the parent (mutation lives in
 * ClusterTab so `useMutation` can invalidate the nodes query); this
 * dialog is purely the safety gate.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, AlertTriangle } from 'lucide-react';
import type { ClusterNode } from '@/types';
import { clusterApi } from '@/api/client';

interface Props {
  siteId: string;
  node: ClusterNode;
  /** Called when the operator confirms — parent runs the drain mutation. */
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export default function DrainConfirmDialog({ siteId, node, onConfirm, onCancel, isLoading }: Props) {
  const nodeIp = node.addresses.find((a) => a.type === 'InternalIP')?.address ?? node.name;
  const [confirmInput, setConfirmInput] = useState('');

  const { data: preview, isLoading: previewLoading, error: previewError } = useQuery({
    queryKey: ['drain-preview', siteId, nodeIp],
    queryFn: () => clusterApi.getDrainPreview(siteId, nodeIp),
    // Drain preview is volatile (pods come and go); never cache.
    staleTime: 0,
    gcTime: 0,
  });

  const criticalCount = preview?.criticalCount ?? 0;
  const requiresTypeName = criticalCount > 0;
  const canConfirm =
    !!preview && !isLoading && (!requiresTypeName || confirmInput === node.name);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-lg mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <div className="flex items-center gap-2">
            {requiresTypeName && <AlertTriangle size={16} className="text-status-error" />}
            <h3 className="text-silver-bright font-semibold">Drain Node</h3>
          </div>
          <button onClick={onCancel} className="text-silver-muted hover:text-silver-bright transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          <div className="bg-surface-deep/50 rounded-lg px-4 py-3">
            <div className="text-xs text-silver-muted">Target Node</div>
            <div className="text-sm text-silver-bright font-mono">{node.name}</div>
          </div>

          {previewLoading && (
            <div className="text-sm text-silver-muted">Loading preview…</div>
          )}

          {previewError && (
            <div className="text-sm text-status-error">
              Failed to load drain preview:{' '}
              {previewError instanceof Error ? previewError.message : String(previewError)}
            </div>
          )}

          {preview && (
            <div className="space-y-3">
              <p className="text-sm text-silver-muted">
                Will evict <span className="text-silver-bright font-medium">{preview.totalEvictable}</span> pods.
                {criticalCount > 0 && (
                  <>
                    {' '}
                    <span className="text-status-error font-medium">{criticalCount}</span> are part of show-affecting workloads.
                  </>
                )}
              </p>

              {preview.pods.length > 0 && (
                <div className="max-h-60 overflow-y-auto bg-surface-deep/50 rounded-lg border border-surface-elevated">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-silver-muted">
                        <th className="text-left px-3 py-2 font-medium">Pod</th>
                        <th className="text-left px-3 py-2 font-medium">Namespace</th>
                        <th className="text-left px-3 py-2 font-medium">Kind</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.pods.map((p) => (
                        <tr
                          key={`${p.namespace}/${p.name}`}
                          data-testid="drain-preview-pod"
                          data-show-affecting={p.showAffecting ? 'true' : 'false'}
                          className={`border-t border-surface-elevated ${p.showAffecting ? 'bg-status-error/10' : ''}`}
                        >
                          <td className={`px-3 py-1.5 font-mono ${p.showAffecting ? 'text-status-error' : 'text-silver-bright'}`}>
                            {p.name}
                            {p.showAffecting && <span className="ml-2 text-[10px] uppercase tracking-wide">show-affecting</span>}
                          </td>
                          <td className="px-3 py-1.5 text-silver-muted">{p.namespace}</td>
                          <td className="px-3 py-1.5 text-silver-muted">{p.kind}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {requiresTypeName && (
                <div className="space-y-1.5">
                  <label className="text-xs text-silver-muted">
                    Type the node name <span className="font-mono text-silver-bright">{node.name}</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={confirmInput}
                    onChange={(e) => setConfirmInput(e.target.value)}
                    placeholder={node.name}
                    autoFocus
                    className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright font-mono placeholder:text-silver-muted/30 focus:border-gold focus:outline-none"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              requiresTypeName
                ? 'bg-status-error text-white hover:bg-status-error/80'
                : 'bg-gold text-surface-deep hover:bg-gold-light'
            }`}
          >
            {isLoading ? 'Draining…' : 'Drain'}
          </button>
        </div>
      </div>
    </div>
  );
}
